// Редактирование полей долга. Открывается из DebtModal. Все поля опциональны —
// шлём в PATCH только то, что юзер реально поменял (diff vs текущей записи).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { Debt, DebtKind } from '../api/types';
import { DEBT_KINDS } from '../lib/debt-kinds';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { useMainButton } from '../lib/use-main-button';

type Props = {
  debt: Debt;
  onClose: () => void;
};

// Порядок фиксируем здесь, подписи берём из общей карты типов.
const KIND_ORDER: DebtKind[] = [
  'bank',
  'uzum',
  'uzum_zero',
  'installment',
  'overdue',
  'company',
  'personal',
];
const KINDS = KIND_ORDER.map((value) => ({ value, label: DEBT_KINDS[value].label }));

function parseAmountToMinor(raw: string): bigint | null {
  if (!raw) return null;
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}

function minorToMajor(minorStr: string | null | undefined): string {
  if (!minorStr) return '';
  return (BigInt(minorStr) / 100n).toString();
}

function rateToPct(raw: string | null | undefined): string {
  if (!raw) return '';
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n) || n === 0) return '';
  return (n * 100).toFixed(2).replace(/\.?0+$/, '');
}

export function DebtEditSheet({ debt, onClose }: Props): JSX.Element {
  // Поля формы — все строки для удобства <input>. На submit парсим.
  const [title, setTitle] = useState(debt.title);
  const [kind, setKind] = useState<DebtKind>(debt.kind);
  const [lender, setLender] = useState(debt.lender ?? '');
  const [principalMajor, setPrincipalMajor] = useState(
    minorToMajor(debt.principal_remaining_minor),
  );
  const [initialPrincipalMajor, setInitialPrincipalMajor] = useState(
    minorToMajor(debt.initial_principal_minor),
  );
  const [monthlyMajor, setMonthlyMajor] = useState(minorToMajor(debt.monthly_payment_minor));
  const [nominalRatePct, setNominalRatePct] = useState(rateToPct(debt.nominal_rate));
  const [totalPayments, setTotalPayments] = useState(
    debt.total_payments != null ? String(debt.total_payments) : '',
  );
  const [paymentDay, setPaymentDay] = useState(
    debt.payment_day != null ? String(debt.payment_day) : '',
  );
  const [openedAt, setOpenedAt] = useState(debt.opened_at ?? '');
  const [dueDate, setDueDate] = useState(debt.due_date ?? '');
  const [note, setNote] = useState(debt.note ?? '');

  const queryClient = useQueryClient();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  const saveMut = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateDebt(debt.id, patch),
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось сохранить изменения')),
    onSettled: () => setSubmitting(false),
  });

  const busy = submitting || saveMut.isPending;

  function buildPatch(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    if (title.trim() && title.trim() !== debt.title) patch.title = title.trim();
    if (kind !== debt.kind) patch.kind = kind;
    if (lender.trim() !== (debt.lender ?? '')) patch.lender = lender.trim() || null;
    if (note.trim() !== (debt.note ?? '')) patch.note = note.trim() || null;

    const newPrincipal = parseAmountToMinor(principalMajor);
    if (newPrincipal !== null && newPrincipal.toString() !== debt.principal_remaining_minor) {
      patch.principal_remaining_minor = newPrincipal.toString();
    }
    const newInitial = initialPrincipalMajor ? parseAmountToMinor(initialPrincipalMajor) : null;
    if ((newInitial?.toString() ?? null) !== (debt.initial_principal_minor ?? null)) {
      patch.initial_principal_minor = newInitial?.toString() ?? null;
    }
    const newMonthly = parseAmountToMinor(monthlyMajor);
    if (newMonthly !== null && newMonthly.toString() !== debt.monthly_payment_minor) {
      patch.monthly_payment_minor = newMonthly.toString();
    }

    if (nominalRatePct) {
      const pct = Number.parseFloat(nominalRatePct.replace(',', '.'));
      if (Number.isFinite(pct) && pct >= 0) {
        const fraction = pct / 100;
        if (fraction.toFixed(4) !== Number.parseFloat(debt.nominal_rate).toFixed(4)) {
          patch.nominal_rate = fraction;
        }
      }
    }

    const totalNum = totalPayments ? Number(totalPayments) : null;
    if ((totalNum ?? null) !== (debt.total_payments ?? null)) {
      patch.total_payments = totalNum;
    }
    const dayNum = paymentDay ? Number(paymentDay) : null;
    if ((dayNum ?? null) !== (debt.payment_day ?? null)) {
      patch.payment_day = dayNum;
    }
    const newOpenedAt = openedAt || null;
    if (newOpenedAt !== (debt.opened_at ?? null)) {
      patch.opened_at = newOpenedAt;
    }
    const newDueDate = dueDate || null;
    if (newDueDate !== (debt.due_date ?? null)) {
      patch.due_date = newDueDate;
    }
    return patch;
  }

  const patch = buildPatch();
  const hasChanges = Object.keys(patch).length > 0;

  const submitSave = (): void => {
    if (!hasChanges || busy) return;
    setSubmitting(true);
    saveMut.mutate(patch);
  };

  useMainButton({
    text: hasChanges ? 'Сохранить изменения' : 'Нет изменений',
    onClick: submitSave,
    enabled: hasChanges && !busy,
    progress: busy,
  });

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">Редактировать: {debt.title}</div>

        <label className="form-field">
          <span className="form-label">Название</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="form-input"
            disabled={busy}
          />
        </label>

        <label className="form-field">
          <span className="form-label">Тип</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as DebtKind)}
            className="form-select"
            disabled={busy}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field">
          <span className="form-label">Кредитор</span>
          <input
            type="text"
            value={lender}
            onChange={(e) => setLender(e.target.value)}
            placeholder="опционально"
            className="form-input"
            disabled={busy}
          />
        </label>

        <label className="form-field">
          <span className="form-label">Текущий остаток ({debt.currency})</span>
          <input
            type="text"
            inputMode="decimal"
            value={principalMajor}
            onChange={(e) => setPrincipalMajor(e.target.value)}
            className="form-input"
            disabled={busy}
          />
        </label>

        <label className="form-field">
          <span className="form-label">Начальная сумма (для прогресса)</span>
          <input
            type="text"
            inputMode="decimal"
            value={initialPrincipalMajor}
            onChange={(e) => setInitialPrincipalMajor(e.target.value)}
            placeholder="опционально"
            className="form-input"
            disabled={busy}
          />
          <span className="form-hint">Нужна для прогресс-бара «выплачено N%».</span>
        </label>

        <label className="form-field">
          <span className="form-label">Платёж/месяц</span>
          <input
            type="text"
            inputMode="decimal"
            value={monthlyMajor}
            onChange={(e) => setMonthlyMajor(e.target.value)}
            className="form-input"
            disabled={busy}
          />
        </label>

        <label className="form-field">
          <span className="form-label">Годовая ставка, %</span>
          <input
            type="text"
            inputMode="decimal"
            value={nominalRatePct}
            onChange={(e) => setNominalRatePct(e.target.value)}
            placeholder="0 = беспроцентный"
            className="form-input"
            disabled={busy}
          />
        </label>

        <div className="form-row">
          <label className="form-field">
            <span className="form-label">Всего платежей</span>
            <input
              type="number"
              min="0"
              max="1000"
              value={totalPayments}
              onChange={(e) => setTotalPayments(e.target.value)}
              className="form-input"
              disabled={busy}
            />
          </label>
          <label className="form-field">
            <span className="form-label">День платежа (1-31)</span>
            <input
              type="number"
              min="1"
              max="31"
              value={paymentDay}
              onChange={(e) => setPaymentDay(e.target.value)}
              className="form-input"
              disabled={busy}
            />
          </label>
        </div>

        <label className="form-field">
          <span className="form-label">Дата открытия</span>
          <input
            type="date"
            value={openedAt}
            onChange={(e) => setOpenedAt(e.target.value)}
            className="form-input"
            disabled={busy}
          />
          <span className="form-hint">
            Нужна вместе с «День платежа» чтобы вычислить «следующий платёж».
          </span>
        </label>

        <label className="form-field">
          <span className="form-label">Срок возврата</span>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="form-input"
            disabled={busy}
          />
          <span className="form-hint">
            Когда долг нужно закрыть целиком. Показывается в списке; у беспроцентных — дата, после
            которой начнут капать проценты.
          </span>
        </label>

        <label className="form-field">
          <span className="form-label">Заметка</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="form-input form-textarea"
            rows={2}
            placeholder="опционально"
            disabled={busy}
          />
        </label>

        <div className="sheet-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={submitSave}
            disabled={!hasChanges || busy}
          >
            {busy ? 'Сохраняю…' : hasChanges ? 'Сохранить изменения' : 'Нет изменений'}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
